// Dependencies
import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';

// Context
import { AppContext } from '../../context';

// Constants (imported from config directly: the contants barrel imports the pages)
import { HUBSPOT_PORTAL_ID, HUBSPOT_REGION, SUPPORT_FORM_ID, SUPPORT_PORTAL_URL } from '../../contants/config';

// Styles
import styles from './styles';

export const FORM_READY_EVENT = 'hs-form-event:on-ready';
export const EMBED_SCRIPT_ID = 'hs-forms-embed';
// Developer embed: renders the form inline (no iframe), so it can be styled with the app's CSS
export const EMBED_SCRIPT_SRC = `https://js-${HUBSPOT_REGION}.hsforms.net/forms/embed/developer/${HUBSPOT_PORTAL_ID}.js`;

// HubSpot's loader initialises once per page and then watches the DOM for new form frames,
// so the script is added a single time and kept across route changes.
const loadEmbedScriptOnce = (onError: () => void) => {
  if (document.getElementById(EMBED_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = EMBED_SCRIPT_ID;
  script.src = EMBED_SCRIPT_SRC;
  script.defer = true;
  script.onerror = () => {
    // Drop the failed tag so the next visit tries again
    script.remove();
    onError();
  };
  document.body.appendChild(script);
};

// Support page: link to the HubSpot service portal and the embedded HubSpot ticket form
export const SupportPage: FC = () => {
  // Context
  const { account } = useContext(AppContext);

  // States
  const [formFailed, setFormFailed] = useState<boolean>(false);
  const [formReady, setFormReady] = useState<boolean>(false);

  // Refs
  const formHostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = formHostRef.current;
    if (!host) return;
    setFormReady(false);

    // Register before loading the script: HubSpot emits on-ready once, right after rendering
    const handleFormReady = (event: Event) => {
      if ((event as CustomEvent).detail?.formId !== SUPPORT_FORM_ID) return;
      setFormReady(true);

      const form = (window as any).HubSpotFormsV4?.getFormFromEvent(event);
      if (!form) return;

      try {
        if (account?.email) form.setFieldValue('0-1/email', account.email);
        if (account?.id) form.setFieldValue('0-5/finance_kunden_id', account.id);
      } catch (e) {
        console.warn('[Support] Formularfelder konnten nicht vorbelegt werden', e);
      }
    };
    window.addEventListener(FORM_READY_EVENT, handleFormReady);

    // The layout's perfect-scrollbar reads activeElement.contentDocument on every keydown, which is
    // null for a cross-origin iframe and throws. Guard kept in case HubSpot renders the form in a frame again.
    const shieldFormFrameKeys = (event: KeyboardEvent) => {
      const active = document.activeElement;
      if (active?.tagName === 'IFRAME' && host.contains(active)) event.stopPropagation();
    };
    window.addEventListener('keydown', shieldFormFrameKeys, true);

    // The loader only scans a few levels below newly added nodes, so the container is added directly
    const frame = document.createElement('div');
    frame.className = 'hs-form-html';
    frame.dataset.region = HUBSPOT_REGION;
    frame.dataset.formId = SUPPORT_FORM_ID;
    frame.dataset.portalId = HUBSPOT_PORTAL_ID;
    host.appendChild(frame);

    loadEmbedScriptOnce(() => setFormFailed(true));

    return () => {
      window.removeEventListener(FORM_READY_EVENT, handleFormReady);
      window.removeEventListener('keydown', shieldFormFrameKeys, true);
      host.replaceChildren();
    };
  }, [account?.email, account?.id]);

  return (
    <Stack spacing={24} sx={{ maxWidth: 720 }}>
      <Paper sx={{ p: 24 }}>
        <Typography variant="h5" sx={{ mb: 8 }}>
          Service portal
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 16 }}>
          Track the status of all your requests in the CIXON service portal. The portal has its own login.
        </Typography>
        <Button
          variant="contained"
          href={SUPPORT_PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNew />}
        >
          Open service portal
        </Button>
      </Paper>
      <Paper sx={{ p: 24 }}>
        <Typography variant="h5" sx={{ mb: 8 }}>
          Submit a request
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 16 }}>
          Your request goes straight to the CIXON support team as a ticket.
        </Typography>
        {formFailed && (
          <Typography variant="body1" color="error" sx={{ mb: 16 }}>
            The form could not be loaded. Please submit your request in the service portal.
          </Typography>
        )}
        <Box ref={formHostRef} sx={(theme) => styles.formHost(theme, { loading: !formReady && !formFailed })} />
      </Paper>
    </Stack>
  );
};
